import { PrinterOutlined } from "@ant-design/icons";
import { Button, Modal, message } from "antd";
import html2canvas from "html2canvas";
import { useRef, useState, useEffect, FC } from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { getEmpresa } from "../../../../services/empresaService";
import { formatDate } from "../../../../utils/dateUtils";
import "../../InvoiceStyles.css";
import { Company } from "../../../../interfaces/company";
import "./InvoiceTicket.css";
import { formatDecimals, numberToLetters } from "../../../../utils/utils";

interface InvoiceTicketProps {
  open: boolean;
  onClose: any;
  invoice: any;
  lines: any;
  caja: string;
  turno: number | string;
  paymentLines: any[];
  selectedClient: any;
  tpvNo: any;
  selectedTPV: any;
}

const InvoiceTicket: FC<InvoiceTicketProps> = ({
  open,
  onClose,
  invoice,
  lines,
  caja,
  turno,
  paymentLines = [],
  selectedClient
}) => {
  const componentRef = useRef(null);
  const { currentUser } = useAuth() as any;
  const [empresaData, setEmpresaData] = useState<Company | null>(null);

  // Cargar datos de la empresa
  useEffect(() => {
    const loadEmpresaData = async () => {
      try {
        const empresa = await getEmpresa();
        setEmpresaData(empresa);
      } catch (error) {
        console.error("Error al cargar datos de la empresa:", error);
      }
    };

    if (open) {
      loadEmpresaData();
    }
  }, [open]);

  // Obtener el descuento máximo de las formas de pago
  const getPaymentMethodDiscount = () => {
    if (!paymentLines || paymentLines.length === 0) return 0;

    // Si hay múltiples formas de pago, no aplicar descuento global
    if (paymentLines.length > 1) return 0;

    // Buscar el descuento más alto entre las formas de pago (solo para una forma de pago)
    const maxDiscount = paymentLines.reduce(
      (max: any, line: any) => ((line.Dto || 0) > max ? line.Dto || 0 : max),
      0,
    );

    return maxDiscount;
  };

  // Calcular subtotal, descuento e impuesto
  const calculateTotals = () => {
    if (!lines || lines.length === 0)
      return {
        subtotal: 0,
        discount: 0,
        tax: 0,
        net: 0,
        paymentDiscount: 0,
        finalTotal: 0,
      };

    // Calcular subtotal sin descuentos
    const subtotalSinDescuento = lines.reduce((sum: number, line: any) => {
      const quantity = line.Quantity || 0;
      const unitPrice = line.Unit_Price || 0;
      return sum + quantity * unitPrice;
    }, 0);

    // Calcular descuento total (sumando todos los descuentos de línea)
    const discount = lines.reduce((sum: number, line: any) => {
      const quantity = line.Quantity || 0;
      const unitPrice = line.Unit_Price || 0;
      const discountPercent = line.Line_Discount_ || 0;
      const discountAmount = quantity * unitPrice * (discountPercent / 100);
      return sum + discountAmount;
    }, 0);

    // Subtotal después de descuentos
    const subtotal = subtotalSinDescuento - discount;

    // Impuesto (IVA) - calculado por línea según el IVA de cada producto
    const tax = lines.reduce((sum: number, line: any) => {
      const quantity = line.Quantity || 0;
      const unitPrice = line.Unit_Price || 0;
      const discountPercent = line.Line_Discount_ || 0;
      const discountAmount = unitPrice * (discountPercent / 100);
      const finalPrice = unitPrice - discountAmount;
      const lineSubtotal = quantity * finalPrice;

      // Aplicar IVA específico del producto
      const lineIva = line.PorIva || 0;
      const lineVat = lineSubtotal * (lineIva / 100);

      return sum + lineVat;
    }, 0);

    // Neto (subtotal + impuesto)
    const net = subtotal + tax;

    // Descuento de forma de pago
    const paymentDiscount = getPaymentMethodDiscount();
    const paymentDiscountAmount =
      paymentDiscount > 0 ? net * (paymentDiscount / 100) : 0;

    // Calcular total final
    let finalTotal;
    if (paymentLines && paymentLines.length > 1) {
      // Si hay múltiples formas de pago, el total final es la suma de las formas de pago
      finalTotal = paymentLines.reduce(
        (sum: number, line: any) => sum + (line.Amount || 0),
        0,
      );
    } else {
      // Si hay una sola forma de pago o ninguna, aplicar el descuento tradicional
      finalTotal = net - paymentDiscountAmount;
    }

    return {
      subtotal: parseFloat(subtotal.toFixed(2)),
      discount: parseFloat(discount.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      net: parseFloat(net.toFixed(2)),
      paymentDiscount: parseFloat(paymentDiscount.toFixed(2)),
      paymentDiscountAmount: parseFloat(paymentDiscountAmount.toFixed(2)),
      finalTotal: parseFloat(finalTotal.toFixed(2)),
    };
  };

  const {
    subtotal,
    discount,
    tax,
    net,
    paymentDiscount,
    paymentDiscountAmount,
    finalTotal,
  } = calculateTotals();

  // Función para convertir el ticket a imagen usando html2canvas
  const convertToImage = async () => {
    try {
      if (!componentRef.current) return null;

      const ticket = componentRef.current as HTMLElement;

      const clone = ticket.cloneNode(true) as HTMLElement;

      clone.style.position = "fixed";
      clone.style.left = "-10000px";
      clone.style.top = "0";
      clone.style.background = "#fff";
      clone.style.width = "74mm";

      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false
      });

      document.body.removeChild(clone);

      return canvas;

    } catch (error) {
      console.error("Error al convertir a imagen:", error);
      return null;
    }
  };

  // Función para imprimir directamente la imagen
  const printImage = async () => {
    if (!componentRef.current) return;

    try {
      const element = componentRef.current as HTMLElement;

      // 1. Convertir el componente a Canvas con ALTA RESOLUCIÓN
      // Usamos offsetWidth y offsetHeight para capturar el tamaño exacto del contenido
      const canvas = await html2canvas(element, {
        scale: 3,
        logging: false,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: element.offsetWidth,
        height: element.offsetHeight,
      });

      // 2. Obtener imagen como data URL
      const image = canvas.toDataURL("image/png");

      // 3. Crear ventana para impresión
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        (window as any).messageApi?.warning(
          "El navegador bloqueó la ventana emergente. Por favor, permítelas para imprimir."
        );
        return;
      }

      // 4. Escribir HTML optimizado
      printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ticket de factura ${invoice?.No || ""}</title>
          <style>
            /* Elimina la paginación y ajusta el tamaño al contenido real */
            @page { 
              margin: 0; 
              size: 80mm auto; /* Forzamos ancho de ticket y alto automático */
            }

            html, body {
              margin: 0;
              padding: 0;
              width: 80mm;
              height: auto;
              background-color: white;
            }

            body {
              display: block;
            }

            img {
              width: 100%;
              height: auto;
              display: block;
              margin: 0;
              padding: 0;
              /* Mejora la nitidez en impresoras térmicas */
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
            }

            /* Aseguramos que el navegador no intente cortar la imagen */
            @media print {
              header, footer { display: none !important; }
              
              html, body {
                height: auto !important;
                overflow: visible !important;
              }

              img {
                break-inside: avoid;
                page-break-inside: avoid;
                page-break-after: avoid;
                page-break-before: avoid;
              }
            }
          </style>
        </head>
        <body>
          <img src="${image}" alt="Ticket" />
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                // Cerrar ventana después de imprimir o cancelar
                window.onafterprint = function() { window.close(); };
                // Respaldo de cierre
                setTimeout(function() { window.close(); }, 1000);
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

      printWindow.document.close();

      // 5. Feedback y cerrar modal principal
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error("Error al imprimir:", error);
      (window as any).messageApi?.error("Error al procesar la imagen del ticket");
    }
  };
  // Función para descargar la imagen del ticket
  const downloadTicketImage = async () => {
    try {
      const canvas = await convertToImage();
      if (!canvas) return;

      // Convertir canvas a blob
      const image = canvas.toDataURL("image/png");

      // Crear enlace para descarga
      const link = document.createElement("a");
      link.download = `ticket-${invoice?.No || invoice?.Posting_Date.replace(/\//g, "-")
        }.png`;
      link.href = image;
      link.click();

      (window as any).messageApi.success("Imagen descargada correctamente");
    } catch (error) {
      console.error("Error al descargar imagen:", error);
      (window as any).messageApi.error("Error al descargar la imagen");
    }
  };

  return (
    <Modal
      title="Previsualización de Ticket"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="download" onClick={downloadTicketImage}>
          Descargar Imagen
        </Button>,
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={printImage}
        >
          Imprimir
        </Button>,
      ]}
      width={400}
      centered
      className="ticket-preview-modal"
    >
      <div className="ticket-container">
        <div className="ticket-content" ref={componentRef}>
          {/* Cabecera del ticket */}
          <div className="ticket-header">
            <div className="text-center">
              <div className="ticket-title">EDITORIAL SANTILLANA S.A.</div>
              <div className="ticket-subtitle">
                R.T.N:{" " + empresaData?.VAT_Registration_No || "Sin RUC"}
              </div>
              <div className="ticket-text-1">
                BOULEVARD SUYAPA METROPOLIS 20501, TEGUCIGALPA M.D.C., HONDURAS
              </div>
              <div className="ticket-text-1">
                COL. LA CAÑADA, 150 MTS DEL ANILLO PERIFERICO, TEGUCIGALPA
                M.D.C.,
              </div>
              <div className="ticket-text-2">
                CENTRO COMERCIAL METROPLAZA, BLVD DEL NORTE, LOCALES 3,4 Y 5 S
              </div>
              <div className="ticket-text-2">
                AVENIDA SAN ISIDRO, PLAZA LAS AMERICAS, LA CIERA, ATLANTIDA
              </div>

              <div className="ticket-text-2"> gerenciahn@santillana.com</div>

              <div className="ticket-row">
                <span className="text-label">C.A.I:</span>
                <span>
                  {invoice?.No_Autorizacion_CAI || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">RANGO AUTORIZADO:</span>
                <div className="text-value">
                  <div>
                    {" "}
                    {`${invoice?.Establecimiento || invoice?.Establecimiento_Factura || ""}-${invoice?.Punto_de_Emision || invoice?.Punto_de_Emision_Factura || ""}-${invoice?.Tipo_Comprobante || ""}-${invoice?.Starting_No || ""}`}
                  </div>
                  <div>
                    {" "}
                    Al {`${invoice?.Establecimiento || invoice?.Establecimiento_Factura || ""}-${invoice?.Punto_de_Emision || invoice?.Punto_de_Emision_Factura || ""}-${invoice?.Tipo_Comprobante || ""}-${invoice?.Ending_No || ""}`}
                  </div>
                </div>
              </div>

              <div className="ticket-row">
                <span className="text-label">FECHA LIMITE EMISIÓN:</span>
                <span className="text-value">
                  {formatDate(invoice?.Fecha_Caducidad)}
                </span>
              </div>


              <div className="ticket-divider"></div>


              <div className="ticket-doc-block">
                <div className="doc-type">
                  {invoice?.Document_Type === "Credit Memo"
                    ? "NOTA DE CRÉDITO"
                    : "FACTURA"}
                </div>

                <div className="doc-number">
                  {`${invoice?.Establecimiento || invoice?.Establecimiento_Factura || ""}-${invoice?.Punto_de_Emision || invoice?.Punto_de_Emision_Factura || ""}-${invoice?.Tipo_Comprobante || ""}-${invoice?.No_Comprobante_Fiscal || ""}`}
                </div>
              </div>

              <div className="ticket-row">
                <span className="text-label">FECHA EMISIÓN:</span>
                <span className="text-value">
                  {formatDate(invoice?.Posting_Date)}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">VENDEDOR:</span>
                <span className="text-value">
                  {invoice?.Salesperson_Code || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">CLIENTE:</span>
                <span className="text-value">
                  {invoice?.Bill_to_Name || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">R.T.N.:</span>
                <span className="text-value">
                  {invoice?.RTN || invoice?.VAT_Registration_No || selectedClient?.Numero_Identificacion_fiscal || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">COLEGIO:</span>
                <span className="text-value">{invoice?.colegioNombre || ""}</span>
              </div>

              <div className="ticket-row">
                <span className="text-label">ALUMNO:</span>
                <span className="text-value">
                  {invoice?.Sell_to_Customer_Name_2 || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">RAZÓN SOCIAL:</span>
                <span className="text-value">
                  {invoice?.Razon_Social || "-"}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">CAJA:</span>
                <span className="text-value">
                  {(caja || invoice?.caja || "1") +
                    " - TURNO " +
                    (turno || invoice?.turno || "1")}
                </span>
              </div>

              <div className="ticket-row">
                <span className="text-label">USUARIO:</span>
                <span className="text-value">
                  {currentUser?.username || "Usuario Sistema"}
                </span>
              </div>
            </div>

            <div className="divider"></div>

            {/* Mostrar factura de referencia para notas de crédito */}
            {invoice?.Document_Type === "Credit Memo" &&
              invoice?.Applies_to_Doc_No && (
                <div className="text-center">
                  <div className="ticket-subsection-title">
                    DATOS DEL COMPROBANTE FISCAL RELACIONADO
                  </div>

                  <div className="ticket-row">
                    <span className="text-label">FECHA EMISIÓN:</span>
                    <span className="text-value">
                      {formatDate(invoice?.Posting_Date) || "-"}
                    </span>
                  </div>

                  <div className="ticket-row">
                    <span className="text-label">C.A.I.:</span>
                    <span className="text-value">
                      {currentUser?.toDo || "-"}
                    </span>
                  </div>

                  <div className="ticket-row">
                    <span className="text-label">No.:</span>
                    <span className="text-value">
                      {invoice.Applies_to_Doc_No || "-"}
                    </span>
                  </div>

                  <div className="ticket-row">
                    <span className="text-label">MOTIVO EMISIÓN DE N.C.:</span>
                    <span className="text-value">ANULACIÓN</span>
                  </div>
                  <div className="ticket-divider"></div>
                </div>
              )}
          </div>

          {/* Tabla de productos */}
          <div className="ticket-products">
            <div className="ticket-subsection-title">Venta</div>

            {/* HEADER */}
            <div className="header-top">
              <span className="col-code">Código Item</span>
              <span className="col-desc">Descripción del item</span>
            </div>

            <div className="header-bottom">
              <span>Cant</span>
              <span>P. Unit.</span>
              <span>Desc.</span>
              <span>Rebaja</span>
              <span>Total</span>
            </div>

            {lines.map((l: any, i: number) => (
              <div key={i} className="product-block">

                <div className="product-desc">
                  <span className="col-code">{l.No}</span>
                  <span className="col-desc">{l.Description}</span>
                </div>

                <div className="product-values">
                  <span className="col-qty">
                    {formatDecimals(l.Quantity, 2, undefined, true)}
                  </span>

                  <span className="col-price">
                    {formatDecimals(l.Unit_Price, 2, undefined, true)}
                  </span>

                  <span className="col-disc">
                    {formatDecimals(
                      l.Quantity * l.Unit_Price * (l.Line_Discount_ / 100), 2, undefined, true
                    )}
                  </span>

                  <span className="col-rebaja">0.00</span>

                  <span className="col-total">
                    L. {formatDecimals(
                      l.Quantity * l.Unit_Price -
                      l.Quantity * l.Unit_Price * (l.Line_Discount_ / 100),
                      2, undefined, true
                    )}
                  </span>
                </div>

              </div>
            ))}
          </div>
          <div className="ticket-divider"></div>

          <div className="ticket-totals">
            <div className="totals-row">
              <span>SUBTOTAL EXENTO:</span>
              <span className="amount">L. {formatDecimals(subtotal, 2, undefined, true)}</span>
            </div>

            <div className="totals-row">
              <span>SUBTOTAL GRAVADO ISV 15%:</span>
              <span className="amount">L. 0.00</span>
            </div>

            <div className="totals-row">
              <span>SUBTOTAL:</span>
              <span className="amount">L. {formatDecimals(subtotal, 2, undefined, true)}</span>
            </div>

            <div className="totals-row">
              <span>Impuesto exonerado:</span>
              <span className="amount">L. 0.00</span>
            </div>

            <div className="totals-row">
              <span>Total descuento otorgado:</span>
              <span className="amount">L. {formatDecimals(discount, 2, undefined, true)}</span>
            </div>

            <div className="totals-row">
              <span>Total rebaja otorgada:</span>
              <span className="amount">L. 0.00</span>
            </div>

            <div className="totals-row">
              <span>IMPUESTO 15% ISV:</span>
              <span className="amount">L. {formatDecimals(tax, 2, undefined, true)}</span>
            </div>

            {/* FINAL TOTAL */}
            <div className="total-final">
              <span className="total-label">TOTAL:</span>
              <span className="final-amount">L. {formatDecimals(net, 2, undefined, true)}</span>
            </div>

            <div>
              <div>MONTO EN LETRAS:</div>
              <div className="num-letters">
                {`*** ${numberToLetters(net).toUpperCase()} LEMPIRAS CON ${(Math.round(net * 100) % 100)
                  .toString()
                  .padStart(2, "0")}/100 ***`}
              </div>
            </div>
          </div>

          {/* Formas de pago usadas */}
          {paymentLines && paymentLines.length > 0 && (
            <>
              <div style={{ marginTop: "18px" }}>
                <div className="pay-title">FORMAS DE PAGO:</div>
                {paymentLines.map((line: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <span>
                      {line.Payment_Method}
                      {line.Dto > 0 &&
                        paymentLines.length <= 1 &&
                        ` (Dto: ${line.Dto}%)`}
                    </span>
                    <span className="bold">
                      L. {formatDecimals(line.Amount, 2, undefined, true)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="ticket-note-container">
            <div className="ticket-note-1 ">GRACIAS POR TU COMPRA!!</div>

            <div className="ticket-note-2">
              LA FACTURA ES BENEFICIO DE TODOS, EXÍJALA
              <br />
              ORIGINAL CLIENTE, COPIA OBLIGADO TRIBUTARIO EMISOR
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default InvoiceTicket;
